import { ToggleSwitch } from '@/components/ToggleSwitch';
import {
  useCreateCategory,
  useDeleteCategory,
  useDeleteMenu,
  useMenuTree,
  useReorderCategories,
  useUpdateCategory,
  useUpdateMenu,
} from '@/features/menu/hooks/useMenu';
import { getErrorT, getT, useT } from '@/i18n';
import { sectionRouter } from '@/utils/navigateMain';
import EmptyState from '@/components/EmptyState';
import ScheduleEditor from '@/features/menu/components/ScheduleEditor';
import { MenuProductRow } from '@/features/menu/components/MenuProductRow';
import { MenuSectionTitle } from '@/features/menu/components/MenuSectionTitle';
import {
  canOpenPricing,
  type ProductView,
  type SectionView,
} from '@/features/menu/utils/menu-draft';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import { AiDock } from '@/features/ai-changeset/components/AiDock';
import { MenuChecklistChip } from '@/features/menu/components/MenuChecklistChip';
import { MenuLegendOffer } from '@/features/menu/components/MenuLegendOffer';
import { MenuImportReviewBanner } from '@/features/menu/components/MenuImportReviewBanner';
import { useQueryClient } from '@tanstack/react-query';
import { useMenuChecklist } from '@/features/menu/hooks/useMenuChecklist';
import { useCreateMenuFromSources } from '@/features/menu/hooks/useCreateMenuFromSources';
import { firstIssueRow } from '@/features/menu/utils/menu-checklist';
import { resolveErrorText } from '@/api/errors';
import { runPositions } from '@/features/ai-changeset/utils/changeset-view';
import { useMenuQuickEdit } from '@/features/menu/hooks/useMenuQuickEdit';
import { BulkAddProductsPopup } from '@/features/menu/pages/BulkAddProductsPage';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { compactLocalized, loc } from '@/utils/localized';
import { setUnsavedDirty } from '@/utils/unsavedChanges';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import type { Category, CategorySchedule } from '@restaurant/api-contracts';
import { type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Popover,
  Preloader,
  Searchbar,
  Subnavbar,
  f7,
} from 'framework7-react';

interface Props {
  f7route: { params: { menuId: string } };
}

const MUTED = '#8e8e93';

function fail(e: unknown) {
  f7.dialog.alert(e instanceof Error ? e.message : getT().error);
}
function nav(path: string) {
  sectionRouter()?.navigate(path);
}
// Navigate back after a successful save: clear the unsaved-changes flag first so
// the route leave-guard (MENU_EDIT_GUARD_PATHS) doesn't prompt on our own Save.
function leaveBack() {
  setUnsavedDirty(false);
  sectionRouter()?.back();
}

// ─── Section editor (create / edit a category) ───────────────────────────────
// A pushed slide-in page (same idiom as the option/choice/pricing editors in
// ProductPage) rather than a bottom sheet. The routed SectionEditPage below
// wraps CategorySheetContent with a navbar whose Save calls submitRef.

function CategorySheetContent({
  open,
  menuId,
  category,
  onClose,
  submitRef,
}: {
  open: boolean;
  menuId: string;
  category: Category | null;
  onClose: () => void;
  submitRef: { current: () => void };
}) {
  const t = useT();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const editing = !!category;

  const [name, setName] = useState<Record<string, string>>({});
  const [description, setDescription] = useState<Record<string, string>>({});
  const [schedule, setSchedule] = useState<CategorySchedule | null>(null);
  // Order channels this section is HIDDEN on (empty = shown everywhere). The UI
  // below is inverted to per-channel "show" toggles for clarity.
  const [hiddenChannels, setHiddenChannels] = useState<Category['hiddenChannels']>([]);
  const setChannelShown = (channel: Category['hiddenChannels'][number], shown: boolean) =>
    setHiddenChannels((prev) =>
      shown
        ? prev.filter((c) => c !== channel)
        : prev.includes(channel)
          ? prev
          : [...prev, channel],
    );
  // Synchronous guard so a rapid double-tap on Save can't create two sections.
  const submittingRef = useRef(false);

  // Sync from props ON OPEN (Rule 10) - the first mount captures placeholders.
  useEffect(() => {
    if (!open) return;
    if (category) {
      setName({ ...(category.name as Record<string, string>) });
      setDescription({ ...((category.description as Record<string, string> | null) ?? {}) });
      setSchedule((category.schedule as CategorySchedule | null | undefined) ?? null);
      setHiddenChannels([...(category.hiddenChannels ?? [])]);
    } else {
      setName({});
      setDescription({});
      setSchedule(null);
      setHiddenChannels([]);
    }
  }, [open, category]);

  // Prompt "Discard changes?" if the user navigates away with unsaved edits -
  // same guard the option/choice editors use (paired with MENU_EDIT_GUARD_PATHS).
  const initial = category
    ? {
        name: { ...(category.name as Record<string, string>) },
        description: { ...((category.description as Record<string, string> | null) ?? {}) },
        schedule: (category.schedule as CategorySchedule | null | undefined) ?? null,
        hiddenChannels: [...(category.hiddenChannels ?? [])],
      }
    : { name: {}, description: {}, schedule: null, hiddenChannels: [] };
  useUnsavedGuard(
    open &&
      JSON.stringify({ name, description, schedule, hiddenChannels }) !== JSON.stringify(initial),
  );

  // Latest-closure submit handler exposed to the wrapper's Save link.
  submitRef.current = () => {
    if (submittingRef.current) return;
    if (!name.en?.trim()) {
      f7.dialog.alert(t.menu.englishNameRequired);
      return;
    }
    const namePayload = compactLocalized(name)!;
    const descPayload = compactLocalized(description) ?? null;
    submittingRef.current = true;
    const onSettled = () => {
      submittingRef.current = false;
    };

    if (editing && category) {
      updateCategory.mutate(
        {
          categoryId: category.id,
          body: { name: namePayload, description: descPayload, schedule, hiddenChannels },
        },
        { onSuccess: onClose, onError: fail, onSettled },
      );
    } else {
      createCategory.mutate(
        {
          menuId,
          body: {
            name: namePayload,
            description: descPayload ?? undefined,
            schedule: schedule ?? undefined,
            hiddenChannels,
          },
        },
        { onSuccess: onClose, onError: fail, onSettled },
      );
    }
  };

  return (
    <PageContent>
      <List strongIos insetIos dividersIos>
        <ListInput
          label={t.menu.categoryName}
          type="text"
          required
          validate
          value={name.en ?? ''}
          onInput={(e) => setName({ ...name, en: (e.target as HTMLInputElement).value })}
          errorMessage={t.menu.nameRequired}
          errorMessageForce={!name.en?.trim()}
        />
        <ListInput
          label={t.menu.categoryDescription}
          type="textarea"
          resizable
          value={description.en ?? ''}
          onInput={(e) =>
            setDescription({ ...description, en: (e.target as HTMLTextAreaElement).value })
          }
        />
      </List>

      <ScheduleEditor
        // Include whether a schedule is loaded in the key: `schedule` is synced
        // from `category` in a post-mount effect, so on first render it is still
        // null and ScheduleEditor would mount "off". The `-sched/-none` suffix
        // flips once the effect populates it, remounting the editor to re-seed
        // from the real windows (correct days/times) instead of a blank state.
        key={`${category?.id ?? 'new'}-${open}-${schedule ? 'sched' : 'none'}`}
        value={schedule}
        onChange={setSchedule}
      />

      {/* Per-channel visibility: hide this whole section on pickup and/or
          delivery independently. Toggles are shown as "show on X" for clarity;
          off adds the channel to hiddenChannels. */}
      <BlockTitle>{t.menu.channelVisibility}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem title={t.menu.showOnPickup}>
          <ToggleSwitch
            slot="after"
            checked={!hiddenChannels.includes('PICKUP')}
            onChange={(v) => setChannelShown('PICKUP', v)}
          />
        </ListItem>
        <ListItem title={t.menu.showOnDelivery}>
          <ToggleSwitch
            slot="after"
            checked={!hiddenChannels.includes('DELIVERY')}
            onChange={(v) => setChannelShown('DELIVERY', v)}
          />
        </ListItem>
      </List>
      <BlockFooter>{t.menu.channelVisibilityHelp}</BlockFooter>
    </PageContent>
  );
}

// Pushed slide-in page for creating/editing a section (categoryId === 'new' to
// create). Registered as a route in src/routes.ts; opened via nav() from the
// menu page. Save lives in the navbar and drives CategorySheetContent.submitRef.
export function SectionEditPage({
  f7route,
}: {
  f7route: { params: { menuId: string; categoryId: string } };
}) {
  const t = useT();
  const { menuId, categoryId } = f7route.params;
  const isNew = categoryId === 'new';
  const { data: tree } = useMenuTree(menuId);
  const submitRef = useRef<() => void>(() => {});

  const category = isNew ? null : (tree?.categories.find((c) => c.id === categoryId) ?? null);

  return (
    <Page pageContent={false}>
      <Navbar title={isNew ? t.menu.newCategory : t.menu.editCategory} backLink={t.back}>
        <NavRight>
          <F7Link onClick={() => submitRef.current()}>{isNew ? t.createShort : t.saveShort}</F7Link>
        </NavRight>
      </Navbar>
      {isNew || category ? (
        <CategorySheetContent
          open
          menuId={menuId}
          category={category}
          onClose={leaveBack}
          submitRef={submitRef}
        />
      ) : (
        <PageContent>
          <Block className="text-align-center">
            <Preloader />
          </Block>
        </PageContent>
      )}
    </Page>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function MenuPage({ f7route }: Props) {
  const t = useT();
  const { menuId } = f7route.params;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: tree, isLoading, error, refetch } = useMenuTree(menuId);
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const reorderCategories = useReorderCategories();
  const updateMenu = useUpdateMenu();
  const deleteMenu = useDeleteMenu();

  const [reorderMode, setReorderMode] = useState(false);
  const [query, setQuery] = useState('');
  // The subnavbar collapses to a "current section" chip + a search button; the
  // full search field expands over it and Cancel restores the collapsed bar.
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentCatId, setCurrentCatId] = useState<string | null>(null);
  const canUseAi = useCanUseAi();
  const quickEdit = useMenuQuickEdit(menuId, tree, () => ({
    sectionId: searchOpen ? null : currentCatId,
    search: query.trim() || null,
  }));
  const checklist = useMenuChecklist(menuId);
  const qc = useQueryClient();
  const menuImport = canUseAi ? quickEdit.menuImport : null;
  const reading = menuImport?.status === 'reading';
  const reviewable = menuImport?.status === 'ready' && menuImport.sources.length > 0;
  const openImportReview = () => sectionRouter()?.navigate(`/menu/${menuId}/import-review`);
  const sectionEls = useRef(new Map<string, HTMLElement>());
  const scrollerRef = useRef<HTMLElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  // Quick add-items popup (name + price rows + section picker), opened from the
  // Add-item entry points.
  const [bulkAdd, setBulkAdd] = useState<{ open: boolean; categoryId?: string }>({ open: false });
  // Guard against rapid double-taps firing the same navigation/popup twice
  // (which would stack duplicate section pages or re-open the add popup).
  const actionLock = useRef(false);
  const once = (fn: () => void) => {
    if (actionLock.current) return;
    actionLock.current = true;
    fn();
    window.setTimeout(() => {
      actionLock.current = false;
    }, 500);
  };
  // Section create/edit is a pushed slide-in page (SectionEditPage), not a
  // bottom sheet - navigate to it; 'new' means create.
  const goSection = (category: Category | null) =>
    once(() => nav(`/menu/${menuId}/category/${category ? category.id : 'new'}`));

  const categories = (tree?.categories ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

  // Client-side item search - the whole menu tree is already loaded, so filter in
  // memory (no server round-trip). A section is kept when its own name matches
  // (then all its items show) or any of its items match name/label/allergen.
  const q = query.trim().toLowerCase();
  const sectionMatches = (c: SectionView) => loc(c.category.name).toLowerCase().includes(q);
  const productMatches = (view: ProductView) =>
    [
      loc(view.product.name),
      view.product.code ?? '',
      ...view.product.labels,
      ...view.product.allergens,
      ...view.product.allergenCodes,
    ]
      .join(' ')
      .toLowerCase()
      .includes(q);
  const { changesOnly, draft } = quickEdit;
  const visibleProducts = (c: SectionView) =>
    (!q || sectionMatches(c) ? c.products : c.products.filter(productMatches)).filter(
      (p) => !changesOnly || p.changed,
    );
  const searchedSections = !q
    ? draft.sections
    : draft.sections.filter((c) => sectionMatches(c) || c.products.some(productMatches));
  const shownCategories = changesOnly
    ? searchedSections.filter((c) => (!q && c.changed) || visibleProducts(c).length > 0)
    : searchedSections;

  const shownCats = shownCategories.map((c) => c.category);
  const shownIds = shownCats.map((c) => c.id).join(',');

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || shownCats.length === 0) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const threshold = (barRef.current?.getBoundingClientRect().bottom ?? 0) + 12;
      let current = shownCats[0].id;
      for (const c of shownCats) {
        const el = sectionEls.current.get(c.id);
        if (el && el.getBoundingClientRect().top <= threshold) current = c.id;
      }
      setCurrentCatId(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    compute();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownIds]);

  useEffect(() => {
    if (!searchOpen) return;
    const id = window.setTimeout(() => {
      document
        .querySelector<HTMLInputElement>('.page[data-name="menu-detail"] .subnavbar input')
        ?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [searchOpen]);

  const jumpToCategory = (id: string) => {
    const el = sectionEls.current.get(id);
    const scroller = scrollerRef.current;
    if (!el || !scroller) return;
    const barBottom = barRef.current?.getBoundingClientRect().bottom ?? 0;
    const top = scroller.scrollTop + el.getBoundingClientRect().top - barBottom - 8;
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    setCurrentCatId(id);
  };

  const openJumpPicker = (e: ReactMouseEvent) => {
    if (shownCats.length === 0) return;
    f7.popover.open('.menu-jump-popover', e.currentTarget as HTMLElement);
  };

  const currentCat = shownCats.find((c) => c.id === currentCatId) ?? shownCats[0];

  const openFirstIssue = () => {
    if (!checklist.data) return;
    const ordered = shownCategories.flatMap((c) => visibleProducts(c).map((p) => p.product.id));
    const id = firstIssueRow(ordered, checklist.data.issues);
    const row = id
      ? scrollerRef.current?.querySelector<HTMLElement>(`[data-view-row="${id}"]`)
      : null;
    if (!row) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.classList.add('menu-item-row--attention');
    window.setTimeout(() => row.classList.remove('menu-item-row--attention'), 1600);
  };

  const undoCreatedMenu = () => {
    if (deleteMenu.isPending) return;
    deleteMenu.mutate(
      { menuId },
      {
        onSuccess: () => sectionRouter()?.back(),
        onError: (e) =>
          f7.toast.show({ text: resolveErrorText(e, getErrorT()), closeTimeout: 4000 }),
      },
    );
  };

  const renameMenu = () => {
    if (!tree) return;
    f7.dialog.prompt(`${t.menu.menuNamePrompt}: ${loc(tree.name)}`, t.menu.renameMenu, (name) => {
      const v = name.trim();
      if (!v) return;
      updateMenu.mutate(
        { menuId, body: { name: { ...(tree.name as Record<string, string>), en: v } } },
        { onError: fail },
      );
    });
  };
  const removeMenu = () => {
    if (!tree) return;
    if (tree.liveChannels.length > 0) {
      f7.toast.show({ text: t.menu.cantDeleteActiveMenu, closeTimeout: 3000 });
      return;
    }
    f7.dialog.confirm(t.menu.deleteMenuConfirmText(loc(tree.name)), t.menu.deleteMenu, () => {
      deleteMenu.mutate({ menuId }, { onSuccess: () => sectionRouter()?.back(), onError: fail });
    });
  };

  // Run the picked action after the sheet has CLOSED - opening a dialog straight
  // from an action-sheet button races Framework7's shared modal backdrop and the
  // dialog's confirm never fires. See ProductPage OptionSection for the same fix.
  const openMenuActions = (e: ReactMouseEvent) => {
    if (!tree) return;
    // Anchor the sheet to the tapped ellipsis: on wide viewports the global
    // `convertToPopover` needs this targetEl to render (and dismiss on outside
    // click) as an anchored popover; without it the converted popover has no
    // backdrop and never closes. On phones it stays a bottom sheet.
    const targetEl = e.currentTarget as HTMLElement;
    let picked: (() => void) | null = null;
    const pick = (fn: () => void) => () => {
      picked = fn;
    };
    f7.actions
      .create({
        targetEl,
        buttons: [
          [
            { text: loc(tree.name), label: true },
            { text: t.menu.renameMenu, onClick: pick(renameMenu) },
            {
              text: reorderMode ? t.menu.doneReordering : t.menu.reorderSections,
              onClick: pick(() => setReorderMode((m) => !m)),
            },
            {
              text: t.menu.archivedItems,
              onClick: pick(() => sectionRouter()?.navigate(`/menu/${menuId}/archived`)),
            },
            ...(reviewable
              ? [{ text: t.menuImportReview.open, onClick: pick(openImportReview) }]
              : []),
            { text: t.menu.deleteMenu, color: 'red', onClick: pick(removeMenu) },
            { text: t.cancel },
          ],
        ],
        on: {
          closed: () => {
            picked?.();
            picked = null;
          },
        },
      })
      .open();
  };

  // One "+" in the navbar instead of two: opens an action menu to pick what to
  // add. Anchored to the tapped button so it converts to a popover on wide
  // viewports (see openMenuActions).
  const openAddMenu = (e: ReactMouseEvent) => {
    const targetEl = e.currentTarget as HTMLElement;
    let picked: (() => void) | null = null;
    const pick = (fn: () => void) => () => {
      picked = fn;
    };
    f7.actions
      .create({
        targetEl,
        buttons: [
          [
            { text: t.menu.addItem, onClick: pick(() => setBulkAdd({ open: true })) },
            { text: t.menu.addCategory, onClick: pick(() => goSection(null)) },
            { text: t.cancel },
          ],
        ],
        on: {
          closed: () => {
            picked?.();
            picked = null;
          },
        },
      })
      .open();
  };

  const confirmDeleteCategory = (c: Category) =>
    f7.dialog.confirm(t.menu.deleteCategoryConfirm(loc(c.name)), t.menu.deleteCategory, () => {
      deleteCategory.mutate({ categoryId: c.id }, { onError: fail });
    });

  const openCategoryActions = (c: Category, e: ReactMouseEvent) => {
    // Anchor to the tapped ellipsis so the wide-viewport popover conversion has a
    // backdrop and dismisses on outside click (see openMenuActions).
    const targetEl = e.currentTarget as HTMLElement;
    let picked: (() => void) | null = null;
    const pick = (fn: () => void) => () => {
      picked = fn;
    };
    f7.actions
      .create({
        targetEl,
        buttons: [
          [
            { text: loc(c.name), label: true },
            { text: t.menu.editCategory, onClick: pick(() => goSection(c)) },
            {
              text: t.menu.addItem,
              onClick: pick(() => setBulkAdd({ open: true, categoryId: c.id })),
            },
            {
              text: c.isActive ? t.menu.hideCategory : t.menu.showCategory,
              onClick: pick(() =>
                updateCategory.mutate(
                  { categoryId: c.id, body: { isActive: !c.isActive } },
                  { onError: fail },
                ),
              ),
            },
            {
              text: t.menu.deleteCategory,
              color: 'red',
              onClick: pick(() => confirmDeleteCategory(c)),
            },
            { text: t.cancel },
          ],
        ],
        on: {
          closed: () => {
            picked?.();
            picked = null;
          },
        },
      })
      .open();
  };

  // Table rows aren't F7 link anchors, so navigate through the main view's
  // router explicitly (same push behaviour the list `link` prop gave us).
  const goTo = (path: string) => once(() => sectionRouter()?.navigate(path));
  const createFromSources = useCreateMenuFromSources();
  const importFromUrl = (url: string) =>
    createFromSources.mutate(
      { keepItemNumbers: true, sources: [{ kind: 'url', url }] },
      {
        onSuccess: ({ menuId: created }) =>
          sectionRouter()?.navigate(`/menu/${created}`, { reloadCurrent: true }),
        onError: (e) =>
          f7.toast.show({ text: resolveErrorText(e, getErrorT()), closeTimeout: 4000 }),
      },
    );
  const openPricingRef = useRef<(productId: string) => void>(() => {});
  openPricingRef.current = (productId) => goTo(`/menu/${menuId}/product/${productId}/pricing`);
  const [openPricing] = useState(() => (productId: string) => openPricingRef.current(productId));

  const handleSort = (from: number, to: number) => {
    if (from === to) return;
    const next = categories.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    reorderCategories.mutate({ menuId, orderedIds: next.map((c) => c.id) }, { onError: fail });
  };

  return (
    <Page name="menu-detail">
      <Navbar title={tree ? loc(tree.name) : t.menu.title} backLink={t.back}>
        <NavRight>
          {reorderMode ? (
            <F7Link onClick={() => setReorderMode(false)}>{t.menu.doneReordering}</F7Link>
          ) : (
            tree && (
              <>
                {/* One "+" for both add actions - keeps the navbar compact on
                    mobile; the action menu asks item vs category. */}
                <F7Link
                  iconF7="plus"
                  tooltip={t.menu.addItem}
                  onClick={(e: ReactMouseEvent) => openAddMenu(e)}
                />
                <F7Link iconF7="ellipsis" onClick={(e: ReactMouseEvent) => openMenuActions(e)} />
              </>
            )
          )}
        </NavRight>
        {/* Rendered from first paint (not gated on async data) so F7 detects the
            subnavbar at page init and reserves the page-content top padding -
            otherwise the search bar overlaps the list. Hidden only in reorder. */}
        {!reorderMode && (
          <Subnavbar inner={false}>
            {searchOpen ? (
              <Searchbar
                customSearch
                backdrop={false}
                placeholder={t.menu.searchItems}
                value={query}
                onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
                onClickClear={() => setQuery('')}
                onClickDisable={() => {
                  setQuery('');
                  setSearchOpen(false);
                }}
              />
            ) : (
              <div
                ref={barRef}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  gap: 8,
                  padding: '0 12px',
                }}
              >
                {currentCat && (
                  <button
                    type="button"
                    onClick={openJumpPicker}
                    style={{
                      display: 'inline-flex',
                      alignSelf: 'stretch',
                      alignItems: 'center',
                      gap: 4,
                      maxWidth: 'calc(100% - 44px)',
                      padding: '0 8px 0 12px',
                      border: 0,
                      background: 'transparent',
                      color: 'var(--f7-text-color)',
                      font: 'inherit',
                      fontSize: 17,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {loc(currentCat.name)}
                    </span>
                    <i className="f7-icons" style={{ fontSize: 16, opacity: 0.6 }}>
                      chevron_down
                    </i>
                  </button>
                )}
                <F7Link
                  iconF7="search"
                  aria-label={t.menu.searchItems}
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setSearchOpen(true)}
                />
              </div>
            )}
          </Subnavbar>
        )}
      </Navbar>

      {canUseAi && tree && !reorderMode && (
        <AiDock
          dock={quickEdit}
          testId="menu-ai-dock"
          title={t.aiDock.menuTitle}
          placeholder={t.menuQuickEdit.placeholder}
          onUndoCreated={tree.liveChannels.length === 0 ? undoCreatedMenu : undefined}
          undoCreatedPending={deleteMenu.isPending}
          onRetryImport={() => goTo('/menu/create-with-ai')}
          onReviewImport={reviewable ? openImportReview : undefined}
          onImportUrl={importFromUrl}
        />
      )}

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {!isLoading && error && (
        <Block>
          <p style={{ color: 'var(--f7-color-red)' }}>
            {error instanceof Error ? error.message : t.menu.failedToLoad}
          </p>
          <Button onClick={() => refetch()}>{t.shared.retry}</Button>
        </Block>
      )}

      {!isLoading && tree && tree.liveChannels.length === 0 && (
        <Block style={{ marginBottom: 0 }}>
          <p style={{ color: MUTED, fontSize: 13 }}>{t.menu.notLiveAnywhereNote}</p>
        </Block>
      )}

      {!isLoading && tree && !reorderMode && checklist.data && (
        <MenuChecklistChip summary={checklist.data} onOpen={openFirstIssue} />
      )}

      {!isLoading && tree && !reorderMode && reviewable && tree.liveChannels.length === 0 && (
        <MenuImportReviewBanner onOpen={openImportReview} />
      )}

      {!isLoading && tree && !reorderMode && menuImport && (
        <MenuLegendOffer
          legend={menuImport.legend}
          onAdded={() => qc.invalidateQueries({ queryKey: ['ai-changeset'] })}
        />
      )}

      {!isLoading && tree && categories.length === 0 && !reading && (
        <Block>
          <EmptyState
            icon="square_grid_2x2"
            title={t.menu.emptyMenu}
            description={t.menu.addCategoryDescription}
          />
          <Button fill round large style={{ marginTop: 16 }} onClick={() => goSection(null)}>
            {t.menu.addCategory}
          </Button>
        </Block>
      )}

      {!isLoading && tree && categories.length > 0 && reorderMode && (
        <>
          <BlockTitle>{t.menu.dragToReorder}</BlockTitle>
          <List
            strongIos
            insetIos
            dividersIos
            sortable
            sortableEnabled
            onSortableSort={(sortData: { from: number; to: number }) =>
              handleSort(sortData.from, sortData.to)
            }
          >
            {categories.map((c) => (
              <ListItem key={c.id} title={loc(c.name)} />
            ))}
          </List>
        </>
      )}

      {!isLoading &&
        tree &&
        categories.length > 0 &&
        !reorderMode &&
        q &&
        shownCategories.length === 0 && (
          <Block>
            <EmptyState
              icon="search"
              title={t.menu.noItemsMatch}
              description={`“${query.trim()}”`}
            />
          </Block>
        )}

      {!isLoading && tree && shownCategories.length > 0 && !reorderMode && (
        <>
          {shownCategories.map((section) => {
            const c = section.category;
            const products = visibleProducts(section);
            const runs = runPositions(products.map((p) => p.changed));
            return (
              <div
                key={c.id}
                ref={(el) => {
                  if (el) {
                    sectionEls.current.set(c.id, el);
                    if (!scrollerRef.current) {
                      scrollerRef.current = el.closest('.page-content');
                    }
                  } else {
                    sectionEls.current.delete(c.id);
                  }
                }}
              >
                {!q && (
                  <MenuSectionTitle
                    view={section}
                    store={quickEdit.navigation.store}
                    onActions={section.status === 'new' ? null : (e) => openCategoryActions(c, e)}
                  />
                )}
                {(products.length > 0 || !changesOnly) && (
                  <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                    {products.map((view, i) =>
                      MenuProductRow({
                        view,
                        run: runs[i] ?? null,
                        store: quickEdit.navigation.store,
                        currency,
                        issues: checklist.data?.issues.get(view.product.id),
                        onOpen:
                          view.status === 'new'
                            ? null
                            : () => goTo(`/menu/${menuId}/product/${view.product.id}`),
                        onPriceClick: canOpenPricing(view) ? openPricing : undefined,
                      }),
                    )}
                  </List>
                )}
              </div>
            );
          })}
        </>
      )}
      {!isLoading && tree && reading && !reorderMode && (
        <div className="menu-skeleton" data-testid="menu-import-skeleton">
          {[4, 3].map((rows, section) => (
            <div key={section}>
              <BlockTitle className="skeleton-text skeleton-effect-wave">
                {'x'.repeat(14)}
              </BlockTitle>
              <List
                strongIos
                strongMd
                insetIos
                insetMd
                dividersIos
                dividersMd
                className="skeleton-text skeleton-effect-wave"
              >
                {Array.from({ length: rows }, (_, row) => (
                  <ListItem key={row} title={'x'.repeat(22)} after={'x'.repeat(5)} />
                ))}
              </List>
            </div>
          ))}
        </div>
      )}

      <BulkAddProductsPopup
        open={bulkAdd.open}
        menuId={menuId}
        categoryId={bulkAdd.categoryId}
        onClose={() => setBulkAdd({ open: false })}
      />
      <Popover className="menu-jump-popover">
        <div
          style={{
            padding: '10px 16px 4px',
            fontSize: 13,
            fontWeight: 600,
            color: MUTED,
          }}
        >
          {t.menu.jumpToSection}
        </div>
        <List strongIos dividersIos style={{ margin: 0, minWidth: 220 }}>
          {shownCats.map((c) => (
            <ListItem
              key={c.id}
              title={loc(c.name)}
              link="#"
              noChevron
              onClick={(ev: ReactMouseEvent) => {
                ev.preventDefault();
                f7.popover.close('.menu-jump-popover');
                jumpToCategory(c.id);
              }}
            >
              {c.id === currentCatId && (
                <i
                  slot="after"
                  className="f7-icons"
                  style={{ color: 'var(--f7-theme-color)', fontSize: 22 }}
                >
                  checkmark
                </i>
              )}
            </ListItem>
          ))}
        </List>
      </Popover>
    </Page>
  );
}

export default MenuPage;
